import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    Box, Typography, Button, Stack, Dialog, IconButton, TextField, Checkbox, Chip, LinearProgress, CircularProgress,
} from '@mui/material';
import * as XLSX from 'xlsx';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import http, { apiErrorMessage } from '../../Api/http';
import { CreateVacancy } from '../../Api/Api';
import SearchSelect from '../SearchSelect';

const PRIMARY = '#7C5CFC';
const SOFT = '#F1EEFE';
const SOFT_BORDER = '#C9BEFB';
const LINE = '#E6EAF1';
const field = {
    '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13, bgcolor: '#fff', '& fieldset': { borderColor: '#E5E7EB' }, '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 1.5 } },
};
const btnSolid = { height: 42, px: 2.6, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: 13.5, color: '#fff', bgcolor: PRIMARY, boxShadow: `0 2px 6px ${PRIMARY}40`, '&:hover': { bgcolor: '#6246E0' }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF', boxShadow: 'none' } };
const btnGhost = { height: 42, px: 2.2, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: 13.5, color: '#475569', border: '1px solid #E5E7EB', bgcolor: '#fff', '&:hover': { bgcolor: '#F8FAFC' } };

const norm = (s) => String(s ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

// A sheet looks like a candidate list when its headings include a name and a contact / experience column.
const looksLikeCandidates = (headers) => {
    const h = headers.map(norm);
    return h.some((x) => x.includes('name')) && h.some((x) => x.includes('contact') || x.includes('phone') || x.includes('experience'));
};

// Which department family a role sheet belongs to. `match` words are looked for in the sheet name;
// `depts` are words looked for in the entity's own department names to pick the matching one.
const GROUPS = [
    { label: 'Digital Marketing', match: ['digital marketing', 'performance', 'smm', 'seo', 'linkedin', 'marketer'], depts: ['digital marketing', 'marketing', 'smm', 'seo'] },
    { label: 'IT / Technology', match: ['developer', 'development', 'flutter', 'ui ux', 'telephony', 'it admin', 'product manager'], depts: ['tech', 'it', 'development', 'engineering', 'ui', 'product'] },
    { label: 'Content & Video', match: ['content', 'video', 'cinematographer', 'graphic'], depts: ['content', 'video', 'graphic', 'creative', 'design'] },
    { label: 'Sales & Calling', match: ['bdm', 'sales', 'telecaller', 'freshers'], depts: ['sales', 'business', 'telecall', 'calling'] },
    { label: 'HR', match: ['hr'], depts: ['hr', 'human'] },
    { label: 'Accounts & Legal', match: ['accounts', 'advocate', 'lawyer'], depts: ['account', 'finance', 'legal'] },
    { label: 'Healthcare', match: ['doctor', 'bds', 'nurse', 'avm', 'dental'], depts: ['doctor', 'medical', 'clinic', 'health', 'dental'] },
    { label: 'Education', match: ['teacher', 'academ'], depts: ['teach', 'academ', 'education'] },
    { label: 'Salon & Wellness', match: ['naturals', 'salon', 'food'], depts: ['salon', 'wellness'] },
];
const groupOf = (sheet) => GROUPS.find((g) => g.match.some((m) => (m.length <= 3 ? norm(sheet).split(' ').includes(m) : norm(sheet).includes(m)))) || { label: 'Other', match: [], depts: [] };
const pickDepartment = (group, departments) => departments.find((d) => group.depts.some((w) => norm(d).includes(w))) || '';

// Imports vacancies from a workbook: every sheet is a role, named by the sheet. Sheets are grouped by
// department family and each gets its own department, so one entity's roles can be created at a time
// (the vacancies go to the entity selected in the sidebar).
export default function ImportVacanciesDialog({ open, onClose, departments, existingTitles, entityName, onImported }) {
    const [file, setFile] = useState(null);
    const [rows, setRows] = useState([]); // { key, sheet, title, openings, count, on, exists, group, dept }
    const [parseError, setParseError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState(null);
    const input = useRef(null);

    useEffect(() => {
        if (open) { setFile(null); setRows([]); setParseError(''); setImporting(false); setProgress(0); setResult(null); }
    }, [open]);

    const existing = useMemo(() => new Set((existingTitles || []).map(norm)), [existingTitles]);

    const readFile = async (f) => {
        if (!f) return;
        setParseError('');
        if (!/\.(csv|xlsx|xls)$/i.test(f.name)) { setParseError('Choose a .csv, .xlsx or .xls file.'); return; }
        try {
            const wb = XLSX.read(await f.arrayBuffer(), { type: 'array' });
            // A vacancy list (one role per row, e.g. "Vacancies from HR Sheet.xlsx") is read row by row; any other
            // workbook is read tab by tab, one role per tab.
            const listRows = [];
            wb.SheetNames.forEach((name) => {
                const aoa = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '', blankrows: false });
                const head = (aoa[0] || []).map(norm);
                const col = (re) => head.findIndex((x) => re.test(x));
                const tIdx = col(/role title|vacanc|^role$|^title$/);
                if (tIdx < 0 || looksLikeCandidates(aoa[0] || [])) return;
                const dIdx = col(/department/); const oIdx = col(/openings?/); const cIdx = col(/candidates/); const nIdx = col(/^note/);
                aoa.slice(1).forEach((r, k) => {
                    const title = String(r[tIdx] ?? '').trim().replace(/\s+/g, ' ');
                    if (!title) return;
                    const suggested = dIdx >= 0 ? String(r[dIdx] ?? '').trim() : '';
                    const group = GROUPS.find((g) => norm(g.label) === norm(suggested)) || groupOf(title);
                    const dept = pickDepartment(group, departments);
                    const flagged = nIdx >= 0 && String(r[nIdx] ?? '').trim() !== '';
                    listRows.push({
                        key: `${name}-${k}`, sheet: name, title, openings: Math.max(1, Number(r[oIdx]) || 1), count: Number(r[cIdx]) || 0,
                        exists: existing.has(norm(title)), group: dept ? group.label : 'No department in this entity', dept, isCandidates: true, on: false, flagged,
                    });
                });
            });
            if (listRows.length > 0) { setFile(f); setRows(listRows); return; }
            const found = wb.SheetNames.map((name, i) => {
                const aoa = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '', blankrows: false });
                let h = aoa.slice(0, 10).findIndex((r) => r.filter((c) => String(c).trim() !== '').length >= 2);
                if (h < 0) h = 0;
                const headers = (aoa[h] || []).map((x) => String(x ?? '').trim());
                const count = aoa.slice(h + 1).filter((r) => r.some((c) => String(c).trim() !== '')).length;
                const title = name.trim().replace(/\s+/g, ' ');
                const exists = existing.has(norm(title));
                const group = groupOf(title);
                const isCandidates = looksLikeCandidates(headers);
                return { key: `${i}-${name}`, sheet: name, title, openings: 1, count, exists, group: !isCandidates ? 'Not a candidate sheet' : (pickDepartment(group, departments) ? group.label : 'No department in this entity'), dept: isCandidates ? pickDepartment(group, departments) : '', isCandidates, on: false };
            });
            if (found.length === 0) { setParseError('No sheets found in that file.'); return; }
            setFile(f); setRows(found);
        } catch {
            setParseError('Could not read that file. Check it is a valid spreadsheet.');
        }
    };

    const patch = (key, p) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));
    // Roles whose department does not exist in this entity, and non-candidate sheets, are shown but cannot be used.
    // Every role can be ticked; one ticked without a department is flagged and blocks Create until a department is chosen.
    const unusable = (r) => r.exists || r.group === 'Not a candidate sheet';
    const patchGroup = (label, p) => setRows((rs) => rs.map((r) => (r.group === label && !unusable(r) ? { ...r, ...p } : r)));

    // Groups in a stable order, "not a candidate sheet" last.
    const grouped = useMemo(() => {
        const order = [...GROUPS.map((g) => g.label), 'Other', 'No department in this entity', 'Not a candidate sheet'];
        return order.map((label) => ({ label, items: rows.filter((r) => r.group === label) })).filter((g) => g.items.length);
    }, [rows]);

    const chosen = rows.filter((r) => r.on && !r.exists && r.title.trim());
    // The department is optional: a ticked role without one is created with no department.
    const canImport = chosen.length > 0 && !importing;

    const run = async () => {
        setImporting(true); setProgress(0);
        const failed = [];
        let done = 0;
        for (const r of chosen) {
            try {
                const { data: body } = await http.post(CreateVacancy, {
                    roleTitle: r.title.trim(), openings: Math.max(1, Number(r.openings) || 1), department: r.dept || '', designation: '',
                    experienceRange: '', employmentType: 'Permanent', priority: 'Medium', minSalary: 0, maxSalary: 0, salaryLabel: '',
                    location: '', hiringManager: '', description: '',
                });
                if (body?.error) throw new Error(body.message || 'Rejected');
                done += 1;
            } catch (err) {
                failed.push({ title: r.title, note: err?.response || err?.request ? apiErrorMessage(err, 'Could not create') : err.message });
            }
            setProgress(Math.round(((done + failed.length) / chosen.length) * 100));
        }
        setImporting(false);
        setResult({ added: done, failed });
        if (done > 0) onImported?.(done);
    };

    return (
        <Dialog
            open={open} onClose={() => !importing && onClose()} maxWidth={false}
            slotProps={{
                backdrop: { sx: { backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)' } },
                paper: { sx: { width: 'min(980px, 96vw)', height: 'min(720px, 94vh)', m: 1, borderRadius: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: `1px solid ${LINE}`, boxShadow: '0 30px 70px -20px rgba(15,23,42,0.35)' } },
            }}
        >
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: `1px solid ${LINE}` }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                    <Box sx={{ width: 42, height: 42, borderRadius: '12px', bgcolor: PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 16px -6px rgba(124,92,252,0.6)' }}>
                        <CloudUploadRoundedIcon sx={{ color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>Import Vacancies</Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#64748B' }}>Each sheet becomes a vacancy in {entityName || 'the selected entity'}</Typography>
                    </Box>
                </Stack>
                <IconButton onClick={onClose} disabled={importing} sx={{ bgcolor: '#F1F5F9' }}><CloseRoundedIcon sx={{ fontSize: 19 }} /></IconButton>
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 2.4 }}>
                {result ? (
                    <Box>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0F172A', mb: 2 }}>Import complete</Typography>
                        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                            {[['Created', result.added, '#16A34A'], ['Failed', result.failed.length, '#DC2626']].map(([k, v, c]) => (
                                <Box key={k} sx={{ flex: 1, p: 2, borderRadius: '12px', bgcolor: '#FBFAFF', border: `1px solid ${LINE}`, borderTop: `3px solid ${c}` }}>
                                    <Typography sx={{ fontSize: 28, fontWeight: 800, color: c, lineHeight: 1 }}>{v}</Typography>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#64748B', mt: 0.6 }}>{k}</Typography>
                                </Box>
                            ))}
                        </Stack>
                        {result.failed.map((r) => (
                            <Stack key={r.title} direction="row" sx={{ justifyContent: 'space-between', gap: 2, py: 1, borderBottom: `1px solid ${LINE}` }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{r.title}</Typography>
                                <Typography sx={{ fontSize: 12.5, color: '#DC2626', textAlign: 'right' }}>{r.note}</Typography>
                            </Stack>
                        ))}
                    </Box>
                ) : !file ? (
                    <Box>
                        <input ref={input} type="file" hidden accept=".csv,.xlsx,.xls" onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
                        <Box
                            onClick={() => input.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => { e.preventDefault(); setDragOver(false); readFile(e.dataTransfer.files?.[0]); }}
                            sx={{ height: 300, borderRadius: '16px', cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: `2px dashed ${dragOver ? PRIMARY : SOFT_BORDER}`, bgcolor: dragOver ? SOFT : '#FBFAFF', transition: 'all .18s', '&:hover': { borderColor: PRIMARY, bgcolor: SOFT } }}
                        >
                            <Box sx={{ width: 76, height: 76, borderRadius: '20px', bgcolor: PRIMARY, boxShadow: '0 10px 24px -10px rgba(124,92,252,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.8 }}>
                                <CloudUploadRoundedIcon sx={{ fontSize: 36, color: '#fff' }} />
                            </Box>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A' }}>Drag a file here, or click to browse</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#64748B', mt: 0.4 }}>.xlsx, .xls or .csv · the sheet names become the vacancy titles</Typography>
                        </Box>
                        {parseError && <Typography sx={{ fontSize: 12.5, color: '#DC2626', mt: 1.4, fontWeight: 600 }}>{parseError}</Typography>}
                    </Box>
                ) : (
                    <Box>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.2 }}>
                            <DescriptionRoundedIcon sx={{ fontSize: 18, color: PRIMARY }} />
                            <Typography noWrap sx={{ fontSize: 12.5, color: '#64748B', flex: 1 }}>{file.name} · {fmtSize(file.size)} · {rows.length} sheets</Typography>
                            <Button size="small" onClick={() => input.current?.click()} sx={{ textTransform: 'none', fontWeight: 700, color: PRIMARY }}>Change</Button>
                            <input ref={input} type="file" hidden accept=".csv,.xlsx,.xls" onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
                        </Stack>
                        <Typography sx={{ fontSize: 12.5, color: '#64748B', mb: 1.8 }}>
                            The roles are grouped by department. Tick the roles to create. The department is optional — pick one if you want it set. Tick only the ones that belong to <strong style={{ color: '#0F172A' }}>{entityName || 'this entity'}</strong>, and set the department on a row if needed. Switch the entity in the sidebar to import another entity&apos;s roles.
                        </Typography>

                        {grouped.map((g) => {
                            const live = g.items.filter((r) => !unusable(r));
                            const all = live.length > 0 && live.every((r) => r.on);
                            return (
                                <Box key={g.label} sx={{ border: `1px solid ${LINE}`, borderRadius: '12px', overflow: 'hidden', mb: 1.6 }}>
                                    <Stack direction="row" sx={{ alignItems: 'center', px: 1.2, py: 0.5, bgcolor: '#F4F3FB' }}>
                                        {live.length > 0 && (
                                            <Checkbox size="small" checked={all} indeterminate={!all && live.some((r) => r.on)} onChange={(e) => patchGroup(g.label, { on: e.target.checked })} sx={{ color: SOFT_BORDER, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: PRIMARY } }} />
                                        )}
                                        <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 800, color: '#4C1D95', letterSpacing: 0.4, pl: live.length === 0 ? 1.2 : 0 }}>{g.label.toUpperCase()}{g.label === 'No department in this entity' ? ' · CHOOSE A DEPARTMENT' : ''}</Typography>
                                        {g.label !== 'Not a candidate sheet' && departments.length > 0 && g.items.some((r) => !unusable(r)) && (
                                            <Box sx={{ width: 230, mr: 1.2 }}>
                                                <SearchSelect
                                                    value="" sx={field} searchPlaceholder="Search departments…"
                                                    onChange={(e) => { const d = e.target.value; if (d) setRows((rs) => rs.map((r) => (r.group === g.label && !unusable(r) ? { ...r, dept: d, on: true } : r))); }}
                                                    options={[{ value: '', label: 'Set department for all…', muted: true }, ...departments.map((d) => ({ value: d, label: d }))]}
                                                />
                                            </Box>
                                        )}
                                        <Typography sx={{ fontSize: 11.5, color: '#6E6B99', pr: 1 }}>{g.items.length} sheet{g.items.length === 1 ? '' : 's'}</Typography>
                                    </Stack>
                                    {g.items.map((r) => (
                                        <Stack key={r.key} direction="row" sx={{ alignItems: 'center', px: 1.2, py: 0.5, borderTop: `1px solid ${LINE}`, opacity: unusable(r) ? 0.55 : 1 }}>
                                            <Checkbox size="small" checked={r.on} disabled={unusable(r)} onChange={(e) => patch(r.key, { on: e.target.checked })} sx={{ color: SOFT_BORDER, '&.Mui-checked': { color: PRIMARY } }} />
                                            <Box sx={{ flex: 1.3, minWidth: 0, pr: 1.2 }}>
                                                <TextField size="small" fullWidth value={r.title} disabled={unusable(r)} onChange={(e) => patch(r.key, { title: e.target.value })} sx={field} />
                                            </Box>
                                            <Box sx={{ width: 190, pr: 1.2 }}>
                                                <SearchSelect
                                                    value={r.dept} disabled={unusable(r)} onChange={(e) => patch(r.key, { dept: e.target.value, ...(r.group === 'No department in this entity' ? { on: Boolean(e.target.value) } : {}) })} sx={field}
                                                    searchPlaceholder="Search departments…"
                                                    options={[{ value: '', label: 'Department…', muted: true }, ...departments.map((d) => ({ value: d, label: d }))]}
                                                />
                                            </Box>
                                            <Box sx={{ width: 76, pr: 1 }}>
                                                <TextField size="small" type="number" value={r.openings} disabled={unusable(r)} onChange={(e) => patch(r.key, { openings: e.target.value })} sx={field} slotProps={{ htmlInput: { min: 1 } }} />
                                            </Box>
                                            <Box sx={{ width: 96 }}>
                                                {r.exists ? <Chip size="small" label="Already open" sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#FEF3C7', color: '#B45309' }} /> : <Typography sx={{ fontSize: 12, color: '#64748B' }}>{r.count} row{r.count === 1 ? '' : 's'}</Typography>}
                                            </Box>
                                        </Stack>
                                    ))}
                                </Box>
                            );
                        })}
                        <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>
                            Columns: role title · department · openings · candidates in that sheet. The department is suggested from the role when this entity has a matching one; otherwise choose it. Vacancies are created as Permanent, medium priority.
                        </Typography>
                        {importing && (
                            <Box sx={{ mt: 2 }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155', mb: 0.6 }}>Creating vacancies… {progress}%</Typography>
                                <LinearProgress variant="determinate" value={progress} sx={{ height: 7, borderRadius: 5, bgcolor: '#EEF1F6', '& .MuiLinearProgress-bar': { bgcolor: PRIMARY } }} />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            <Stack direction="row" sx={{ justifyContent: 'flex-end', gap: 1, px: 3, py: 1.8, borderTop: `1px solid ${LINE}`, bgcolor: '#FBFAFE' }}>
                {result ? (
                    <Button onClick={onClose} sx={btnSolid}>Done</Button>
                ) : (
                    <>
                        <Button onClick={onClose} disabled={importing} sx={btnGhost}>Cancel</Button>
                        {file && (
                            <Button onClick={run} disabled={!canImport} endIcon={importing ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null} sx={btnSolid}>
                                {importing ? 'Creating…' : `Create ${chosen.length} vacanc${chosen.length === 1 ? 'y' : 'ies'}`}
                            </Button>
                        )}
                    </>
                )}
            </Stack>
        </Dialog>
    );
}
